import { z } from 'zod';
import { IdSchema, PaginationQuerySchema, paginatedSchema } from './common';

/** Why a stock movement was created. Every movement is immutable. */
export const MOVEMENT_KINDS = [
  'OPENING',
  'RECEIPT',
  'ISSUE',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'WRITE_OFF',
  'STOCKTAKE_SURPLUS',
  'STOCKTAKE_SHORTAGE',
  'MANUAL_ADJUSTMENT',
  'REVERSAL',
] as const;
export const MovementKindSchema = z.enum(MOVEMENT_KINDS);
export type MovementKind = z.infer<typeof MovementKindSchema>;

/** Movement kinds that increase the on-hand quantity. */
export const INBOUND_MOVEMENTS: readonly MovementKind[] = [
  'OPENING',
  'RECEIPT',
  'TRANSFER_IN',
  'STOCKTAKE_SURPLUS',
  'MANUAL_ADJUSTMENT',
  'REVERSAL',
];

export const StockItemSchema = z.object({
  id: IdSchema,
  productId: IdSchema,
  productSku: z.string(),
  productName: z.string(),
  unitShortName: z.string(),
  warehouseId: IdSchema,
  warehouseName: z.string(),
  /** On-hand quantity (always >= 0). */
  quantity: z.string(),
  /** Quantity committed to draft documents. */
  reserved: z.string(),
  /** quantity - reserved. */
  available: z.string(),
  /** Moving-average cost per unit. */
  avgCost: z.string(),
  /** quantity * avgCost. */
  value: z.string(),
  minStock: z.string(),
  isLow: z.boolean(),
  updatedAt: z.string(),
});
export type StockItem = z.infer<typeof StockItemSchema>;

export const StockItemListSchema = paginatedSchema(StockItemSchema);

export const StockMovementSchema = z.object({
  id: IdSchema,
  productId: IdSchema,
  productSku: z.string(),
  productName: z.string(),
  warehouseId: IdSchema,
  warehouseName: z.string(),
  kind: MovementKindSchema,
  /** Signed: positive increases stock, negative decreases it. */
  quantity: z.string(),
  unitCost: z.string(),
  balanceAfter: z.string(),
  documentId: IdSchema.nullable(),
  documentNumber: z.string().nullable().optional(),
  note: z.string().nullable(),
  createdAt: z.string(),
  createdByName: z.string().nullable().optional(),
});
export type StockMovement = z.infer<typeof StockMovementSchema>;

export const StockMovementListSchema = paginatedSchema(StockMovementSchema);

export const StockQuerySchema = PaginationQuerySchema.extend({
  warehouseId: IdSchema.optional(),
  categoryId: IdSchema.optional(),
  productId: IdSchema.optional(),
  lowStockOnly: z.coerce.boolean().optional(),
  /** Only rows with a non-zero quantity. */
  inStockOnly: z.coerce.boolean().optional(),
});
export type StockQuery = z.infer<typeof StockQuerySchema>;
export type StockQueryInput = z.input<typeof StockQuerySchema>;

export const MovementQuerySchema = PaginationQuerySchema.extend({
  warehouseId: IdSchema.optional(),
  productId: IdSchema.optional(),
  kind: MovementKindSchema.optional(),
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
});
export type MovementQuery = z.infer<typeof MovementQuerySchema>;
export type MovementQueryInput = z.input<typeof MovementQuerySchema>;

/** Direct stock correction without a document (permission `stock:write`). */
export const AdjustStockSchema = z.object({
  productId: IdSchema,
  warehouseId: IdSchema,
  /** Target quantity, not a delta. */
  quantity: z.string().regex(/^\d+(\.\d+)?$/, { message: 'validation.nonNegative' }),
  unitCost: z.string().regex(/^\d+(\.\d+)?$/, { message: 'validation.nonNegative' }).optional(),
  note: z.string().trim().max(500).optional(),
});
export type AdjustStock = z.infer<typeof AdjustStockSchema>;

/** Aggregated stock value across the tenant. */
export const StockValuationSchema = z.object({
  totalValue: z.string(),
  totalSkus: z.number().int().nonnegative(),
  totalQuantity: z.string(),
  byWarehouse: z.array(
    z.object({
      warehouseId: IdSchema,
      warehouseName: z.string(),
      value: z.string(),
      skus: z.number().int().nonnegative(),
    }),
  ),
  byCategory: z.array(
    z.object({
      categoryId: IdSchema.nullable(),
      categoryName: z.string(),
      value: z.string(),
    }),
  ),
});
export type StockValuation = z.infer<typeof StockValuationSchema>;
